import { api } from "../utils/api";
import { ModelParams, SolveFor } from "../OptionSolver";
import { useLiveMarketDataStore, useOrderModifyDataStore, useTradesStore } from "./store";
import { useEffect, useState } from "react";
import { DateTime } from "luxon";

// Clickable presets under the Increment field (shared with UnderlyingOrder)
export const INCREMENT_PRESETS: [string, string][] = [
  ["W", "0.01"],
  ["X", "0.05"],
  ["Y", "0.1"],
  ["Z", "0.2"],
];

// Row of clickable words shown under an order field (BID / ASK, increment and size
// presets). Positioned absolutely so it doesn't make the field's column taller than
// its neighbours. Each word is padded with a space that is part of the link (and its
// underline), with no gap between words, so it's easy to hit: on both sides by
// default, or only after the word with `padOneSide`.
export const QuickPicks = ({
  items,
  padOneSide = false,
}: {
  items: { label: string; title: string; onPick: () => void }[];
  padOneSide?: boolean;
}) => (
  <div className={`flex absolute ${padOneSide ? "left-0" : "-left-[1ch]"} top-full whitespace-pre`}>
    {items.map(({ label, title, onPick }) => (
      // a real anchor so hovering behaves like a link; the click is intercepted
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <a
        key={label}
        href="#"
        className="hover:underline"
        title={title}
        onClick={(e) => {
          e.preventDefault();
          onPick();
        }}
      >
        {padOneSide ? `${label} ` : ` ${label} `}
      </a>
    ))}
  </div>
);

export function getIvModelParams(
  contract_name: string,
  optionPrice: string,
  underlyingPrice: number
): ModelParams {
  console.log(
    "Calculating IV for:",
    contract_name,
    optionPrice,
    underlyingPrice
  );

  // calculate iv and total
  // format: TSLA 407.5 C 2025-10-17
  const readable_name_parts = contract_name.split(" ");
  const strike = Number(readable_name_parts[1]);
  const right = readable_name_parts[2];

  const year = Number(readable_name_parts[3].split("-")[0]);
  const month = Number(readable_name_parts[3].split("-")[1]);
  const day = Number(readable_name_parts[3].split("-")[2]);

  const TZ = "America/New_York";

  // Now in NY (string for display)
  const nowNY = DateTime.now().setZone(TZ);

  // Expiry at 16:01 on the given date (as a real instant)
  const expiry = DateTime.fromObject(
    { year: year, month, day, hour: 16, minute: 1, second: 0 },
    { zone: TZ }
  );

  const timeToExpiry = expiry.diff(nowNY, "days").days;

  console.log("Time to expiry (days): ", timeToExpiry);
  console.log("Strike: ", strike);
  console.log("Right: ", right);
  console.log("Underlying price: ", underlyingPrice);
  console.log("Option price: ", optionPrice);

  // check all params are valid
  if (
    isNaN(strike) ||
    isNaN(timeToExpiry) ||
    isNaN(underlyingPrice) ||
    isNaN(Number(optionPrice))
  ) {
    throw new Error("Invalid parameters");
  }

  return {
    u: underlyingPrice,
    vol: 0,
    s: strike,
    r: 0.04,
    t: timeToExpiry,
    type: right === "C" ? "call" : "put",
    price: Number(optionPrice),
    solveFor: SolveFor.vol,
    timeUnits: "d",
  };
}

export const OrderInput = ({
  modifyOrCreateOrder,
}: {
  modifyOrCreateOrder: (e: React.FormEvent) => void;
}) => {
  const orderModifyDataStore = useOrderModifyDataStore();
  const liveMarketDataStore = useLiveMarketDataStore();
  const tradesStore = useTradesStore();
  const currentOrderStatus = tradesStore.trades.find(
    (t) => t.orderId.toString() === orderModifyDataStore.orderModifyData.orderId
  )?.status;
  const [underlyingData, setUnderlyingData] = useState<string>("");
  const [increment, setIncrement] = useState<string>("0.01");
  const selectedQuote = liveMarketDataStore.liveMarketData.find(
    (d) => d.conId.toString() === orderModifyDataStore.orderModifyData.conId
  );

  useEffect(() => {
    orderModifyDataStore.setAction("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const underlying = liveMarketDataStore.liveMarketData.find(
      (d) => d.readable_name.length <= 6
    );

    let info = "";

    if (!underlying) {
      info = "No underlying data";
    } else {
      info = `Underlying: ${underlying.readable_name} [${
        underlying.bid ? underlying.bid.toFixed(2) : ""
      } | ${underlying.last ? underlying.last.toFixed(2) : ""} | ${
        underlying.ask ? underlying.ask.toFixed(2) : ""
      }] (V: ${underlying.volume ? underlying.volume : ""})`;
    }

    setUnderlyingData(info);
  }, [liveMarketDataStore.liveMarketData]);

  return (
    <div>
      <form onSubmit={modifyOrCreateOrder} className="flex gap-4 items-center pb-6">
        {orderModifyDataStore.orderModifyData.orderId !== "" ? (
          <div className="">
            <label className="block" htmlFor="order-id">Editing id:</label>
            <input
              id="order-id"
              type="text"
              disabled
              className="bg-slate-200"
              value={orderModifyDataStore.orderModifyData.orderId}
            />
          </div>
        ) : (
          <div className="flex flex-row gap-4">
            <div>
              <label htmlFor="order-id">New order:</label>
              <input
                id="order-id"
                type="text"
                disabled
                className="bg-slate-200"
                value={orderModifyDataStore.orderModifyData.readable_name}
              />
            </div>

            <div>
              <label>Action:</label>
              <div
                className={`font-bold ${
                  orderModifyDataStore.orderModifyData.action === "BUY"
                    ? "text-blue-600"
                    : orderModifyDataStore.orderModifyData.action === "SELL"
                    ? "text-orange-500"
                    : "text-gray-400"
                }`}
              >
                {orderModifyDataStore.orderModifyData.action || "—"}
              </div>
            </div>
          </div>
        )}

        <div className="relative">
          <label htmlFor="price">Price:</label>
          <input
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            autoCapitalize="off"
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            results={0}
            name="custom_field_price"
            step="any"
            id="price"
            type="number"
            value={orderModifyDataStore.orderModifyData.price}
            onChange={(e) => {
              // only update if the input is in focus (user is typing)
              if (document.activeElement?.id !== "price") {
                return;
              }

              orderModifyDataStore.setPrice(e.target.value);

              orderModifyDataStore.setIv("Calculating...");
            }}
            onKeyDown={(e) => {
              // step="any" (so arbitrary clicked/typed prices stay valid)
              // means the browser's own Up/Down stepping defaults to 1;
              // nudge by the Increment field ourselves instead.
              if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
              e.preventDefault();

              const inc = parseFloat(increment);
              const step = !isNaN(inc) && inc > 0 ? inc : 0.01;
              const current =
                parseFloat(orderModifyDataStore.orderModifyData.price) || 0;
              const next =
                e.key === "ArrowUp" ? current + step : current - step;

              orderModifyDataStore.setPrice(Math.max(0, next).toFixed(2));
              orderModifyDataStore.setIv("Calculating...");
            }}
          />
          <QuickPicks
            items={(["bid", "ask"] as const).map((side) => {
              const quote = selectedQuote?.[side];
              return {
                label: side.toUpperCase(),
                title: quote !== undefined ? quote.toFixed(2) : "No live quote",
                onPick: () => {
                  if (quote === undefined) return;
                  orderModifyDataStore.setPrice(quote.toFixed(2));
                  orderModifyDataStore.setIv("Calculating...");
                },
              };
            })}
          />
        </div>

        <div className="w-24 relative">
          <label htmlFor="increment">Increment:</label>
          <input
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            autoCapitalize="off"
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            results={0}
            name="custom_field_increment"
            step={0.01}
            id="increment"
            type="number"
            value={increment}
            onChange={(e) => {
              // only update if the input is in focus (user is typing)
              if (document.activeElement?.id !== "increment") {
                return;
              }

              setIncrement(e.target.value);
            }}
          />
          <QuickPicks
            padOneSide
            items={INCREMENT_PRESETS.map(([label, value]) => ({
              label,
              title: value,
              onPick: () => setIncrement(value),
            }))}
          />
        </div>

        <div>
          <label htmlFor="iv">IV:</label>
          <input
            id="iv"
            type="text"
            disabled
            className="bg-slate-200"
            value={orderModifyDataStore.orderModifyData.iv}
          />
        </div>

        <div className="relative">
          <label htmlFor="size">Size:</label>
          <input
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            autoCapitalize="off"
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            results={0}
            name="custom_field_size"
            id="size"
            type="number"
            step={1}
            value={orderModifyDataStore.orderModifyData.size}
            onChange={(e) => {
              // only update if the input is in focus (user is typing)
              if (document.activeElement?.id !== "size") {
                return;
              }

              orderModifyDataStore.setSize(e.target.value);
            }}
          />
          <QuickPicks
            padOneSide
            items={[-10, -5, 5, 10].map((delta) => ({
              label: delta > 0 ? `+${delta}` : `${delta}`,
              title: `${delta > 0 ? "Add" : "Subtract"} ${Math.abs(delta)}`,
              onPick: () => {
                const current = parseInt(orderModifyDataStore.orderModifyData.size, 10) || 0;
                orderModifyDataStore.setSize(Math.max(0, current + delta).toString());
              },
            }))}
          />
        </div>

        <div>
          <label htmlFor="total">Total:</label>
          <input
            className="bg-slate-200"
            id="total"
            type="text"
            value={orderModifyDataStore.orderModifyData.total}
            disabled
          />
        </div>

        {currentOrderStatus === "Filled" ? (
          <button type="button" className="btn btn-success mt-5" disabled>Filled</button>
        ) : currentOrderStatus === "Cancelled" ? (
          <button type="button" className="btn btn-danger mt-5" disabled>Cancelled</button>
        ) : (
          <button
            type="submit"
            className="btn btn-default mt-5"
            disabled={
              orderModifyDataStore.orderModifyData.orderId === "" &&
              orderModifyDataStore.orderModifyData.action === ""
            }
          >
            {orderModifyDataStore.orderModifyData.orderId === "" ? "Create" : "Modify"}
          </button>
        )}
      </form>

      <div>{underlyingData}</div>
    </div>
  );
};
